import React, { useEffect, useState } from 'react'
import { Box, Card, CardContent, ToggleButton } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs, { type Dayjs } from 'dayjs'

import { PhoneEntryBox } from '~components/common'
import {
  Dropdown,
  Icon,
  IconButton,
  InputLabelStyled,
  TextField,
  Typography,
} from '~components/internal'
import { DAYS_OF_WEEK, type EnrichedAIPhoneLineConfig, TIMEZONES, phoneWithExtension } from '~types'
import { getUserTimezone } from '~utils/dateUtils'

import GreetingField from './GreetingField'
import { transferLineFields } from './utils'

interface AIPhoneLineConfigCardProps {
  config: EnrichedAIPhoneLineConfig
  index: number
  onUpdate: (index: number, config: EnrichedAIPhoneLineConfig) => void
  onDelete: (index: number) => void
}

const AIPhoneLineConfigCard: React.FC<AIPhoneLineConfigCardProps> = ({
  config,
  index,
  onUpdate,
  onDelete,
}) => {
  const defaultTimezone = getUserTimezone('America/New_York')

  const [transferNumber, setTransferNumber] = useState<string>(
    phoneWithExtension(config.default_transfer_number, config.default_transfer_extension),
  )
  const [_isValidTransferPhone, setIsValidTransferPhone] = useState<boolean>(
    !!config.default_transfer_number,
  )
  const [startTime, setStartTime] = useState<Dayjs | null>(
    config.start_time ? dayjs(`2000-01-01T${config.start_time}`) : dayjs(`2000-01-01T09:00`),
  )
  const [endTime, setEndTime] = useState<Dayjs | null>(
    config.end_time ? dayjs(`2000-01-01T${config.end_time}`) : dayjs(`2000-01-01T17:00`),
  )

  const handleTransferPhoneChange = (newPhoneString: string, newPhoneIsValid: boolean): void => {
    setTransferNumber(newPhoneString)
    setIsValidTransferPhone(newPhoneIsValid)
    onUpdate(index, { ...config, ...transferLineFields(newPhoneString) })
  }

  const handleStartTimeChange = (newValue: Dayjs | null): void => {
    setStartTime(newValue)
    if (newValue) {
      onUpdate(index, { ...config, start_time: newValue.format('HH:mm') })
    }
  }

  const handleEndTimeChange = (newValue: Dayjs | null): void => {
    setEndTime(newValue)
    if (newValue) {
      onUpdate(index, { ...config, end_time: newValue.format('HH:mm') })
    }
  }

  const handleDayToggle = (dayValue: number): void => {
    const newDays = (config.days_of_week as number[]).includes(dayValue)
      ? (config.days_of_week as number[]).filter((d) => d !== dayValue)
      : [...(config.days_of_week as number[]), dayValue]
    onUpdate(index, { ...config, days_of_week: newDays })
  }

  useEffect(() => {
    setTransferNumber(
      phoneWithExtension(config.default_transfer_number, config.default_transfer_extension),
    )
    setIsValidTransferPhone(!!config.default_transfer_number)
    setStartTime(
      config.start_time ? dayjs(`2000-01-01T${config.start_time}`) : dayjs(`2000-01-01T09:00`),
    )
    setEndTime(config.end_time ? dayjs(`2000-01-01T${config.end_time}`) : dayjs(`2000-01-01T17:00`))
  }, [config.id])

  return (
    <Card className='phone-line-config-card'>
      <CardContent>
        <Box className='phone-line-config-card__header'>
          <Box className='phone-line-config-card__title'>
            <Icon name='schedule' />
            <Typography variant='h6'>{config.name || `Configuration ${index + 1}`}</Typography>
            <Box className='priority-badge'>
              <Typography variant='caption'>Priority {index}</Typography>
            </Box>
          </Box>
          <IconButton size='small' color='error' onClick={() => onDelete(index)}>
            <Icon name='delete' />
          </IconButton>
        </Box>

        <Box className='phone-line-config-card__content'>
          <TextField
            label='Configuration Name'
            value={config.name}
            onChange={(e) => onUpdate(index, { ...config, name: e.target.value })}
          />

          <GreetingField
            label='Greeting Message'
            value={config.greeting_message ?? ''}
            onChange={(greeting) => onUpdate(index, { ...config, greeting_message: greeting })}
            placeholder='Enter the greeting message for this time period...'
          />

          <Box className='phone-line-config-card__time-row'>
            <Box>
              <InputLabelStyled shrink className='phone-line-dialog__label'>
                Days of Week
              </InputLabelStyled>
              <Box className='day-toggle-group'>
                {DAYS_OF_WEEK.map((day) => (
                  <ToggleButton
                    key={day.value}
                    value={day.value}
                    selected={(config.days_of_week as number[]).includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                    size='small'
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      },
                    }}
                  >
                    {day.label}
                  </ToggleButton>
                ))}
              </Box>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box className='phone-line-dialog__time-field'>
                <InputLabelStyled shrink className='phone-line-dialog__label'>
                  Start Time
                </InputLabelStyled>
                <TimePicker
                  value={startTime}
                  onChange={handleStartTimeChange}
                  slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                />
              </Box>
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box className='phone-line-dialog__time-field'>
                <InputLabelStyled shrink className='phone-line-dialog__label'>
                  End Time
                </InputLabelStyled>
                <TimePicker
                  value={endTime}
                  onChange={handleEndTimeChange}
                  slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                />
              </Box>
            </LocalizationProvider>

            <Box className='phone-line-config-card__timezone-field'>
              <Dropdown
                label='Timezone'
                value={config.timezone || defaultTimezone}
                options={TIMEZONES}
                onChange={(value) => onUpdate(index, { ...config, timezone: value as string })}
                id='timezone-select'
                fullWidth
              />
            </Box>
          </Box>

          <Box>
            <InputLabelStyled shrink className='phone-line-dialog__label'>
              Default Transfer Line
            </InputLabelStyled>
            <PhoneEntryBox inputPhoneState={transferNumber} onChange={handleTransferPhoneChange} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default AIPhoneLineConfigCard
